import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminBillingGrantsApi,
  BillingGrantPlan,
  BillingGrantReason,
  BillingGrantRefundPolicy,
  type AdminWorkspaceSearchResultDto,
  type BillingGrantConflictDto,
} from '@up2stay/up2promo-api-client';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  cn,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import {
  BILLING_SECTION_LABEL_CLASS,
  BILLING_SECTION_MUTED_CLASS,
  BILLING_SECTION_TITLE_CLASS,
} from '@/core/components/billing/billing-section-card';
import { useEntitlementLabels } from '@/core/components/billing/upgrade-surfaces';
import { ListSearchField } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import { SettingsGroupFooter } from '@/core/components/settings/settings-group';
import { useDebounce } from '@/core/hooks/use-debounce';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { useIsPlatformAdmin } from '@/core/hooks/use-is-platform-admin';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';
import { AppIonPage } from '@/core/components/app-ion-page';

const GRANT_PLANS = [
  BillingGrantPlan.PLUS,
  BillingGrantPlan.TEAMS,
  BillingGrantPlan.ENTERPRISE,
] as const;
const GRANT_REASONS = [
  BillingGrantReason.INTERNAL,
  BillingGrantReason.SPONSORED,
  BillingGrantReason.PILOT,
] as const;
const REFUND_POLICIES = [
  BillingGrantRefundPolicy.PRORATED,
  BillingGrantRefundPolicy.FULL,
  BillingGrantRefundPolicy.NONE,
] as const;

function getGrantFormDefaults(workspace: AdminWorkspaceSearchResultDto) {
  if (workspace.billingGrant) {
    return {
      plan: workspace.billingGrant.plan,
      reason: workspace.billingGrant.reason,
      memberLimit:
        workspace.billingGrant.memberLimit != null
          ? String(workspace.billingGrant.memberLimit)
          : '',
      reputationLocationLimit:
        workspace.billingGrant.reputationLocationLimit != null
          ? String(workspace.billingGrant.reputationLocationLimit)
          : '',
      expiresAt: workspace.billingGrant.expiresAt
        ? workspace.billingGrant.expiresAt.slice(0, 10)
        : '',
    };
  }

  return {
    plan: BillingGrantPlan.TEAMS,
    reason: BillingGrantReason.INTERNAL,
    memberLimit: '',
    reputationLocationLimit: '',
    expiresAt: '',
  };
}

function BillingGrantsBreadcrumb({
  domainLabel,
  pageLabel,
}: {
  domainLabel: string;
  pageLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {pageLabel}
    </>
  );
}

export function BillingGrantsAdminPage() {
  const dict = useDictionary();
  const pageDict = dict.support.billingGrants;
  const { getPlanLabel } = useEntitlementLabels();
  const getErrorMessage = useErrorMessage();
  const isPlatformAdmin = useIsPlatformAdmin();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selected, setSelected] =
    useState<AdminWorkspaceSearchResultDto | null>(null);
  const [plan, setPlan] = useState<BillingGrantPlan>(BillingGrantPlan.TEAMS);
  const [reason, setReason] = useState<BillingGrantReason>(
    BillingGrantReason.INTERNAL,
  );
  const [memberLimit, setMemberLimit] = useState('');
  const [reputationLocationLimit, setReputationLocationLimit] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [refundPolicy, setRefundPolicy] = useState<BillingGrantRefundPolicy>(
    BillingGrantRefundPolicy.PRORATED,
  );

  const applyGrantFormDefaults = (workspace: AdminWorkspaceSearchResultDto) => {
    const defaults = getGrantFormDefaults(workspace);
    setPlan(defaults.plan);
    setReason(defaults.reason);
    setMemberLimit(defaults.memberLimit);
    setReputationLocationLimit(defaults.reputationLocationLimit);
    setExpiresAt(defaults.expiresAt);
  };

  const searchQuery = useQuery({
    queryKey: ['admin-workspace-search', debouncedSearch],
    queryFn: async () => {
      const response = await AdminBillingGrantsApi.searchAdminWorkspaces({
        query: { q: debouncedSearch, limit: 20 },
      });
      return response.data ?? [];
    },
    enabled: debouncedSearch.trim().length >= 2,
  });

  const contextQuery = useQuery({
    queryKey: ['billing-grant-context', selected?.id],
    queryFn: async (): Promise<BillingGrantConflictDto | null> => {
      if (!selected?.id) return null;
      const response = await AdminBillingGrantsApi.getBillingGrantContext({
        path: { workspaceId: selected.id },
      });
      return response.data ?? null;
    },
    enabled: !!selected?.id,
  });

  const upsertGrant = useMutation({
    mutationFn: async () => {
      if (!selected) return null;
      const response = await AdminBillingGrantsApi.upsertBillingGrant({
        path: { workspaceId: selected.id },
        body: {
          plan,
          reason,
          memberLimit: memberLimit ? Number(memberLimit) : undefined,
          reputationLocationLimit: reputationLocationLimit
            ? Number(reputationLocationLimit)
            : undefined,
          expiresAt: expiresAt
            ? new Date(`${expiresAt}T23:59:59.999Z`).toISOString()
            : undefined,
          subscriptionRefundPolicy: contextQuery.data?.hasActiveSubscription
            ? refundPolicy
            : undefined,
        },
      });
      return response.data ?? null;
    },
    onSuccess: (data) => {
      if (data) setSelected(data);
      toast.success(pageDict.saveSuccess);
      void queryClient.invalidateQueries({
        queryKey: ['admin-workspace-search'],
      });
      void contextQuery.refetch();
    },
    onError: (err) => {
      const code =
        getApiValidationErrorCode(err, 'memberLimit') ??
        getApiValidationErrorCode(err, 'reputationLocationLimit') ??
        getApiValidationErrorCode(err, 'subscriptionRefundPolicy') ??
        getApiValidationErrorCode(err, 'stripe');
      toast.error(getErrorMessage(code, { fallback: pageDict.saveError }));
    },
  });

  const revokeGrant = useMutation({
    mutationFn: async () => {
      if (!selected) return;
      await AdminBillingGrantsApi.revokeBillingGrant({
        path: { workspaceId: selected.id },
      });
    },
    onSuccess: async () => {
      toast.success(pageDict.revokeSuccess);
      if (selected) {
        const updated = { ...selected, billingGrant: null };
        setSelected(updated);
        applyGrantFormDefaults(updated);
      }
      void queryClient.invalidateQueries({
        queryKey: ['admin-workspace-search'],
      });
      await contextQuery.refetch();
    },
    onError: () => {
      toast.error(pageDict.revokeError);
    },
  });

  if (!isPlatformAdmin) {
    return <Navigate to={shellPaths.moreHub} replace />;
  }

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="gap-6 pb-8">
          <PageHeader
            breadcrumb={
              <BillingGrantsBreadcrumb
                domainLabel={dict.nav.supportTools}
                pageLabel={dict.nav.billingGrants}
              />
            }
            title={pageDict.title}
            backHref={shellPaths.moreSupport}
          />

          <div className="flex flex-col gap-2">
            <Card className="min-w-0 overflow-hidden">
              <CardContent className="flex flex-col gap-5 p-5 sm:p-6">
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="workspace-search"
                    className={BILLING_SECTION_LABEL_CLASS}
                  >
                    {pageDict.searchLabel}
                  </Label>
                  <ListSearchField
                    id="workspace-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={pageDict.searchPlaceholder}
                    onClear={() => setSearch('')}
                  />
                  {searchQuery.isFetching ? (
                    <p className={BILLING_SECTION_MUTED_CLASS}>
                      {pageDict.searching}
                    </p>
                  ) : null}
                  {searchQuery.data && searchQuery.data.length > 0 ? (
                    <div className="max-h-56 space-y-1 overflow-y-auto rounded-[var(--r-control,11px)] border border-border bg-card p-1">
                      {searchQuery.data.map((workspace) => (
                        <button
                          key={workspace.id}
                          type="button"
                          onClick={() => {
                            setSelected(workspace);
                            applyGrantFormDefaults(workspace);
                          }}
                          className={cn(
                            'flex w-full flex-col rounded-md px-3 py-2 text-left transition-colors hover:bg-[color:var(--ghost-accent-bg)]/40',
                            selected?.id === workspace.id &&
                              'bg-[color:var(--ghost-accent-bg)]',
                          )}
                        >
                          <span className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-foreground">
                            {workspace.name}
                            {workspace.billingGrant ? (
                              <Badge variant="success">
                                {pageDict.grantActive}
                              </Badge>
                            ) : null}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {workspace.username
                              ? `@${workspace.username} · `
                              : ''}
                            {workspace.activeMembers} {pageDict.membersLabel} ·{' '}
                            {workspace.connectedReputationLocations}{' '}
                            {pageDict.locationsLabel}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
            <SettingsGroupFooter>{pageDict.description}</SettingsGroupFooter>
          </div>

          {selected ? (
            <Card className="min-w-0 overflow-hidden">
              <CardContent className="flex flex-col gap-6 p-5 sm:p-6">
                <div className="flex flex-col gap-1">
                  <h2 className={BILLING_SECTION_TITLE_CLASS}>
                    {selected.name}
                  </h2>
                  <p className={BILLING_SECTION_MUTED_CLASS}>
                    {pageDict.selectedWorkspace
                      .replace('{members}', String(selected.activeMembers))
                      .replace(
                        '{locations}',
                        String(selected.connectedReputationLocations),
                      )}
                  </p>
                </div>

                {contextQuery.data?.hasActiveSubscription ? (
                  <Alert>
                    <AlertDescription className="space-y-3">
                      <p className={BILLING_SECTION_MUTED_CLASS}>
                        {pageDict.activeSubscriptionWarning.replace(
                          '{plan}',
                          typeof contextQuery.data.subscriptionPlan === 'string'
                            ? contextQuery.data.subscriptionPlan
                            : 'paid',
                        )}
                      </p>
                      <div className="space-y-2">
                        <Label className={BILLING_SECTION_LABEL_CLASS}>
                          {pageDict.refundPolicyLabel}
                        </Label>
                        <Select
                          value={refundPolicy}
                          onValueChange={(value) =>
                            setRefundPolicy(value as BillingGrantRefundPolicy)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {REFUND_POLICIES.map((policy) => (
                              <SelectItem key={policy} value={policy}>
                                {pageDict.refundPolicies[policy]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className={BILLING_SECTION_LABEL_CLASS}>
                      {pageDict.planLabel}
                    </Label>
                    <Select
                      value={plan}
                      onValueChange={(value) =>
                        setPlan(value as BillingGrantPlan)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GRANT_PLANS.map((grantPlan) => (
                          <SelectItem key={grantPlan} value={grantPlan}>
                            {getPlanLabel(grantPlan as never)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={BILLING_SECTION_LABEL_CLASS}>
                      {pageDict.reasonLabel}
                    </Label>
                    <Select
                      value={reason}
                      onValueChange={(value) =>
                        setReason(value as BillingGrantReason)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GRANT_REASONS.map((grantReason) => (
                          <SelectItem key={grantReason} value={grantReason}>
                            {pageDict.reasons[grantReason]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={BILLING_SECTION_LABEL_CLASS}>
                      {pageDict.memberLimitLabel}
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={memberLimit}
                      onChange={(e) => setMemberLimit(e.target.value)}
                      placeholder={pageDict.limitPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={BILLING_SECTION_LABEL_CLASS}>
                      {pageDict.reputationLimitLabel}
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      value={reputationLocationLimit}
                      onChange={(e) =>
                        setReputationLocationLimit(e.target.value)
                      }
                      placeholder={pageDict.unlimitedPlaceholder}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className={BILLING_SECTION_LABEL_CLASS}>
                      {pageDict.expiresAtLabel}
                    </Label>
                    <Input
                      type="date"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                    />
                  </div>
                </div>

                <Separator />

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => upsertGrant.mutate()}
                    isLoading={upsertGrant.isPending}
                  >
                    {selected.billingGrant
                      ? pageDict.updateGrant
                      : pageDict.applyGrant}
                  </Button>
                  {selected.billingGrant ? (
                    <Button
                      variant="destructive"
                      onClick={() => revokeGrant.mutate()}
                      isLoading={revokeGrant.isPending}
                    >
                      {pageDict.revokeGrant}
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
